import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EyeBlueIcon } from "../../../assets";
import { UserType } from "../../../constants";
import { TaskConstants } from "../../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin, ResultType, SelectData, UpcomingMilestonePropsWithKey } from "../../../models";
import { MapProjectCoordinates } from "../../../models/shared/mapProjectCoordinates";
import {
  getAggregateWorkflowTasks,
  getProjectAggregate,
  GetTaskBoardResponse,
  searchProjects,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../service/query";
import { getTaskBoard } from "../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../service/Shared";
import { DetailsMapConstants } from "../../../templates/PublicDeveloperDetailsTemplate/constants";
import { useAuth } from "../../../useAuth";
import { flattenObject, formatNumber } from "../../../utils";
import { getDealListingRoute, getProjectDetailsByUuid } from "../../../utils/routes";
import {
  ChartData,
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  OverdueTaskProps,
} from "../../../widget";
import { IconCellFormatterData } from "../../../widget/data/DataGrid/models";

interface UseDevelopmentManagerDashboardReturnData {
  phaseChartData: ChartData;
  unitsKey: string | undefined;
  dropDownData: SelectData;
  isLoading: boolean;
  isLoadingTasks: boolean;

  columnsTasks: DataGridColumnDefinition[];
  columnsTeamTasks: DataGridColumnDefinition[];
  tasksDataLoad: DataGridCursorDataLoadEventHandler;
  teamTasksDataLoad: DataGridCursorDataLoadEventHandler;
  currentUserType: UserType;
  overdueTasks: OverdueTaskProps;
  isOverdueTasksPanelOpen: boolean;
  setIsOverdueTasksPanelOpen: (isOpen: boolean) => void;

  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  projectsCoordinates: MapProjectCoordinates[] | undefined;
  isUpcomingMilestonesPanelOpen: boolean;
  setIsUpcomingMilestonesPanelOpen: (isOpen: boolean) => void;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;

  isTasks: boolean;
  hasTasks: (isTasks: boolean) => void;

  isTeamTasks: boolean;
  hasTeamTasks: (isTeamTasks: boolean) => void;

  isYourTasksPanelOpen: boolean;
  setIsYourTasksPanelOpen: (isOpen: boolean) => void;

  isTeamTasksPanelOpen: boolean;
  setIsTeamTasksPanelOpen: (isOpen: boolean) => void;

  handleRefresh: () => void;
  refreshTasks: boolean;

  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;
  onSelectChange: (value: string) => void;
}

const columnsTasks: DataGridColumnDefinition[] = [
  {
    key: "objectLink",
    name: "Workflow",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: true,
    minWidth: 140,
    sortable: true,
  },
  {
    key: "name",
    name: "Task",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
  },
  {
    key: "viewButton",
    name: "View",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
  },
];

const columnsTeamTasks: DataGridColumnDefinition[] = [
  {
    key: "objectLink",
    name: "Workflow",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: true,
    minWidth: 140,
    sortable: true,
  },
  {
    key: "ownerOrganisation.name",
    name: "Owner",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
  {
    key: "name",
    name: "Task",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    minWidth: 100,
  },
  {
    key: "viewButton",
    name: "View",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    minWidth: 50,
  },
];

export const useDashboard = (): UseDevelopmentManagerDashboardReturnData => {
  const navigate = useNavigate();
  const { currentDevelopmentManagerUuid, currentUserType, user } = useAuth();

  const [phaseChartData, setPhaseChartData] = useState<ChartData>({});
  const [dropDownData, setDropDownData] = useState<SelectData>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);

  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<OverdueTaskProps>();
  const [isOverdueTasksPanelOpen, setIsOverdueTasksPanelOpen] = useState(false);
  const [projectsCoordinates, setProjectsCoordinates] = useState<MapProjectCoordinates[] | undefined>();
  const [isUpcomingMilestonesPanelOpen, setIsUpcomingMilestonesPanelOpen] = useState(false);
  const [unitsKey, setUnitsKey] = useState<string | undefined>();

  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number]>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const [isYourTasksPanelOpen, setIsYourTasksPanelOpen] = useState(false);

  const [isTeamTasksPanelOpen, setIsTeamTasksPanelOpen] = useState(false);

  const [isTasks, hasTasks] = useState(false);
  const [isTeamTasks, hasTeamTasks] = useState(false);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const onSelectChange = (selectedKey: string): void => {
    setUnitsKey(selectedKey);
  };

  const fetchChartsData = useCallback(async () => {
    if (!currentDevelopmentManagerUuid) {
      throw new Error(`CurrentDevelopmentManagerUuid has no value. ${currentDevelopmentManagerUuid}`);
    }
    const [phaseAggregateResponse, unitsAggregateResponse] = await Promise.all([
      getProjectAggregate({
        filterBy: "developmentManager",
        filterOperator: "eq",
        filterValue: currentDevelopmentManagerUuid,
        groupBy: "cachedPhase",
        aggregation: "count",
        aggregate: "id",
      }),
      getProjectAggregate({
        filterBy: "developmentManager",
        filterOperator: "eq",
        filterValue: currentDevelopmentManagerUuid,
        aggregation: "sum",
        aggregate: "cached_total_unit",
        groupBy: "unitMeasurement",
      }),
      // JLU: Other available aggregate options KAN-7466
      // {Constants.CachedTotalQuantityAggregate, "cached_total_unit"},
      // {Constants.CachedAvailableForSaleQuanity, "cached_available_for_sale_unit"},
      // {Constants.CachedReservedQuanity, "cached_reserved_unit"},
      // {Constants.CachedSoldQuanity, "cached_sold_unit"},
      Promise.resolve({
        status: Status.Error,
        data: [{ key: "remove", objectName: "remove", value: 1 }],
      }),
    ]);

    // If no data initialise with 0
    if (phaseAggregateResponse.status === Status.Success && phaseAggregateResponse.data) {
      const phaseAggregateData = phaseAggregateResponse.data.reduce<ChartData>((previous, current) => {
        // eslint-disable-next-line no-param-reassign
        previous[current.key] = current.value;
        return previous;
      }, {});
      const phaseAggregateDataReordered = {
        Feasibility: phaseAggregateData.Feasibility ?? 0,
        Design: phaseAggregateData.Design ?? 0,
        Implementation: phaseAggregateData.Implementation ?? 0,
        "Ongoing monitoring": phaseAggregateData["Ongoing Monitoring"] ?? 0,
      };
      setPhaseChartData(phaseAggregateDataReordered);
    }

    if (unitsAggregateResponse.status === Status.Success && unitsAggregateResponse.data) {
      // Dynamically create an array based on the unitsAggregateResponse
      const dropDownSelectData = unitsAggregateResponse.data.map((v) => ({
        key: `${formatNumber(v.value)}-${v.key}`, // key is the actual unit value here
        value: v.key, // value is the display value in the dropdown, which represents by the group by measurement;
      })) as SelectData;

      const totalUnitsKey = `${formatNumber(
        unitsAggregateResponse.data.reduce((sum, v) => {
          return sum + v.value;
        }, 0)
      )}-Overall`;
      dropDownSelectData.unshift({ key: totalUnitsKey, value: "Overall" });

      const carbonIdx = dropDownSelectData.findIndex((item) => item.value === "Carbon");
      if (carbonIdx > 1) {
        const [carbonItem] = dropDownSelectData.splice(carbonIdx, 1);
        dropDownSelectData.splice(1, 0, carbonItem);
      }

      setDropDownData(dropDownSelectData);
      setUnitsKey(totalUnitsKey);
    }
    const tasks = await getAggregateWorkflowTasks({
      activityUuid: null,
      phase: null,
      projectUuid: null,
      workflowUuid: null,
      dealUuid: null,
    });
    const result = await searchWorkflowTasks({
      paging: {
        limit: 4,
        afterCursor: null,
        beforeCursor: null,
      },
      filter: {
        results: {
          isMilestone: {
            operator: "eq",
            value: true,
          },
          deadlineDate: {
            operator: "neq",
            value: null,
          },
          status: {
            operator: "neq",
            value: TaskConstants.TASK_STATUS_COMPLETE,
          },
        },
      },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
    });
    if (result.status !== Status.Success || result.data === undefined) {
      return;
    }
    setOverdueTasks({
      amount: tasks.data?.overdueTasksCount ? tasks.data?.overdueTasksCount.toString() : "0",
      label: "Overdue tasks",
    });
    setUpcomingMilestones(
      result.data.results.map((task) => ({
        key: task.uuid,
        name: task.name,
        deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
        status: task.status,
        assigneeName: task.assignee?.name ?? undefined,
        project: task.workflowObjectName,
        // eslint-disable-next-line no-alert
        onClick: () => {
          setSelectedPanelTask(task);
          setShowWorkflowTaskPanel(true);
        },
      }))
    );

    const projectsRes = await searchProjects({
      paging: {
        limit: DetailsMapConstants.MAXIMUM_MARKERS,
        beforeCursor: null,
        afterCursor: null,
      },
    });
    if (projectsRes.status === Status.Success && projectsRes.data) {
      setProjectsCoordinates(
        projectsRes.data.results
          .filter((project) => project.locationCoordinates !== null)
          .map((project) => ({
            coordinate: project.locationCoordinates as string,
            name: project.name,
          }))
      );
    }
  }, []);

  useEffect(() => {
    fetchChartsData().finally(() => {
      setIsLoading(false);
    });
  }, [fetchChartsData, refreshTasks]);

  const getAndSetWorkflow = useCallback(async (): Promise<void> => {
    const res = await getTaskBoard({
      showComplete: true,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: {
          uuid: {
            operator: "eq",
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            value: selectedPanelTask!.workflowUuid,
          },
        },
      },
    });
    if (res.status === Status.Success && res.data) {
      setWorkflow(res.data.results[0]);
    }
  }, [selectedPanelTask?.workflowUuid]);

  useEffect(() => {
    if (selectedPanelTask?.workflowUuid) {
      getAndSetWorkflow();
    }
  }, [selectedPanelTask?.workflowUuid]);

  const formatData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.viewButton = {
          action: () => {
            setSelectedPanelTask(el);
            setShowWorkflowTaskPanel(true);
          },
          icon: <EyeBlueIcon width={20} height={20} strokeWidth={1.5} />,
        } as IconCellFormatterData;

        const { workflowObjectUuid, workflowObjectType } = el;
        if (workflowObjectUuid) {
          const objectRoute =
            workflowObjectType === "Deal"
              ? getDealListingRoute(workflowObjectUuid, currentUserType)
              : getProjectDetailsByUuid(workflowObjectUuid, currentUserType, { origin: Origin.DashboardPage });
          result.objectLink = {
            text: el.workflowObjectName,
            action: () => {
              navigate(objectRoute);
            },
          } as DataGridButtonLinkCellFormatterData;
        }

        return result;
      }) || [],
    []
  );

  const tasksDataLoad: DataGridCursorDataLoadEventHandler = async ({ paging, filtering }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 4,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsLoadingTasks(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.assignee = {
      uuid: {
        operator: "eq",
        value: user?.userUuid || "",
      },
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    await searchWorkflowTasks({
      paging: {
        limit: 5,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: { results: filterCriteria },
      sort: [
        {
          key: "deadlineDate" as "results.deadlineDate",
          direction: "asc",
        },
      ],
    })
      .then((response) => {
        const formatted = formatData(response.data);
        // Set isTasks to true if there are any tasks, false otherwise
        hasTasks(formatted.length > 0);

        data = {
          resultData: formatted,
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });
    return data;
  };

  const teamTasksDataLoad: DataGridCursorDataLoadEventHandler = async ({
    paging,
    filtering,
    sorting,
  }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 6,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsLoadingTasks(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }
    sorting.forEach((s) => {
      switch (s.key) {
        case "avatar":
          // eslint-disable-next-line no-param-reassign
          s.key = "assignee.name";
          break;
        default:
          break;
      }
    });

    await searchWorkflowTasks({
      paging: {
        limit: 6,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      filter: { results: filterCriteria },
    })
      .then((response) => {
        const formatted = formatData(response.data);
        // Set isTeamTasks to true if there are any tasks, false otherwise
        hasTeamTasks(formatted.length > 0);

        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });
    return data;
  };

  return {
    phaseChartData,
    unitsKey,
    dropDownData,

    isLoading,
    isLoadingTasks,

    columnsTasks,
    columnsTeamTasks,

    tasksDataLoad,
    teamTasksDataLoad,

    currentUserType,
    upcomingMilestones,
    projectsCoordinates,

    overdueTasks: overdueTasks || { amount: "0", label: "Overdue tasks" },
    isOverdueTasksPanelOpen,
    setIsOverdueTasksPanelOpen,

    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,

    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,

    isTeamTasksPanelOpen,
    setIsTeamTasksPanelOpen,

    isTasks,
    hasTasks,

    isTeamTasks,
    hasTeamTasks,

    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
    onSelectChange,
  };
};
